import React, { useContext, useState } from "react";
import { CompanyContext } from "contexts/CompanyContext";
import { Button, Dropdown } from "components/ui";
import { HiDotsHorizontal } from "react-icons/hi";
import TextPreview from "./components/TextPreview";
import MediaPreview from "./components/MediaPreview";
import { HiOutlinePlus, HiOutlineTrash } from "react-icons/hi2";
import AddAssetButton from "./components/AddAssetButton";
import usePostGenerator from "utils/hooks/usePostGenerator";
import { BsStars } from "react-icons/bs";
import { AIContext } from "contexts/AIContext";
import { MagicSpinner } from 'react-spinners-kit'
import { PLACEMENT } from "components/ui/utils/constant";
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const BlueskyPreview = (props) => {
    const { 
        className, 
        isEditable,
        onAssetDelete,
        onAssetEdit,
        onAssetNew,
        onTextEdit, 
        onCreateNetworkPost,
        onDeleteIndexPost,
        allPosts = []
    } = props;

    const network = "bluesky";
    const { company, companySocials } = useContext(CompanyContext);
    const integration = companySocials?.filter(e => e.type == network)?.[0];
    const { generateEmptyPost } = usePostGenerator();
    const [ isLoading, setIsLoading ] = useState(false);
    const { getByQueryChat } = useContext(AIContext);
    
    let posts = [props];
    if ('0' in props) {
        posts = [];
        Object.keys(props).forEach((key) => {
            if (!isNaN(key)) {
                posts.push(props[key]);
            }
        });
    }

    const onCreateFromScratch = () => {
        const newPost = generateEmptyPost(network);
        onCreateNetworkPost({...newPost?.posts?.[0]});
    }

    const generateNewPostInThread = async () => {
        setIsLoading(true);

        const newPost = generateEmptyPost(network);
        const newThread = newPost?.posts?.[0]

        const systemMessage = `
            
            When you are given posts from an ongoing thread, you must write the next post in the series.
        
            INSTRUCTIONS:
            1. The new post must continue the topic, maintain the same tone and style as the previous post.
            2. Fit within the Bluesky character limit (300 characters or less).
            3. Read the last post of the thread to understand the context.
            4. Maintain the same tone (whether formal, casual, humorous, etc.) and the same writing style.
            5. The new post should seamlessly connect to the previous one, keeping the thread coherent and engaging.
            6. Ensure that the post provides valuable information or insight that moves the topic forward.
            
            Skip the preambles: write ONLY the new post and nothing else.
        `

        const prompt = `
            ALL PREVIOUS POSTS:
            ${posts?.map((post, i) => `post ${i + 1}: ${post.text}`).join("\n")}

            Skip the preambles: write ONLY the new post and nothing else.
        
            New post:`;

        const response = await getByQueryChat(prompt, true, false, systemMessage);

        onCreateNetworkPost({...newThread, description: response});
        setIsLoading(false);
    }


    return (
        <div className={`flex flex-col ${className} bluesky-container`}>
            {posts.map((post, index) => {
                let {
                    user_name,
                    user_image,
                    created_at,
                    text,
                    assets,
                    postIndex
                } = post;

                return (
                    <div key={index} className="bluesky-post px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex gap-3 relative">
                            <div className="flex-shrink-0 z-10">
                                <img 
                                    src={user_image || integration?.image?.url || `/img/socials-rounded/bluesky.svg`}
                                    className="w-10 h-10 rounded-full object-cover"
                                    alt={user_name || integration?.username || "User profile"}
                                />
                            </div>

                            <div className={`flex-1 max-w-[100%]`}>
                                <div className="flex items-center justify-between relative z-10">
                                    <div className="flex items-center gap-2 relative z-10">
                                        <span className="font-semibold text-base text-tertiary-700 dark:text-white">{integration?.name || company?.name}</span>
                                        <span className="text-tertiary-400 dark:text-gray-600 text-sm">
                                            @{integration?.username || "handle.bsky.social"}
                                        </span>
                                        <span className="text-tertiary-400 dark:text-gray-600 text-sm">
                                            {!!created_at && (
                                                dayjs().diff(dayjs(created_at), 'day') > 7 
                                                    ? dayjs(created_at).format("DD/MM/YYYY")
                                                    : dayjs(created_at).fromNow(true)
                                            )}
                                        </span>
                                    </div>
                                    {isEditable && posts?.length > 1 && (
                                        <Dropdown
                                            disabled={!isEditable || !(posts?.length > 1)}
                                            toggleClassName={(!isEditable || !(posts?.length > 1)) && `!cursor-default`}
                                            parentClassName="!-mb-2 z-100 relative"
                                            placement={PLACEMENT.BOTTOM_END}
                                            renderTitle={
                                                <Button
                                                    icon={<HiDotsHorizontal />}
                                                    variant="plain"
                                                    size="sm"
                                                    className="text-tertiary-400"
                                                />
                                            }
                                        >
                                            <Dropdown.Item 
                                                onClick={() => onDeleteIndexPost(postIndex)}
                                                className="flex items-center gap-2 py-2 px-4 text-sm"
                                            >
                                                <HiOutlineTrash className="text-red-500"/>
                                                <span>Delete post</span>
                                            </Dropdown.Item>
                                        </Dropdown>
                                    )}
                                </div>

                                {/* Post text */}
                                <TextPreview 
                                    text={text} 
                                    post={post}
                                    network={network} 
                                    isEditable={isEditable}
                                    onTextEdit={(value) => onTextEdit(postIndex, value)}
                                    className="my-2"
                                />
                            </div>
                        </div>

                        {/* Images/Video */}
                        {assets?.length > 0 && (
                            <div className="mt-3 overflow-hidden !-mx-4">
                                <div className={`grid gap-1 pl-16 pr-4 ${
                                    assets.length === 1 ? 'grid-cols-1' : 
                                    assets.length === 2 ? 'grid-cols-2' : 
                                    assets.length === 3 ? 'grid-cols-2' : 
                                    'grid-cols-2'
                                }`}>
                                    {assets.slice(0, 4).map((asset, assetIndex) => (
                                        <div key={assetIndex} className={`rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 ${
                                            assets.length === 1 ? 'h-64' :
                                            assets.length === 3 && assetIndex === 0 ? 'row-span-2 h-full' :
                                            'h-32'
                                        }`}>
                                            <MediaPreview 
                                                asset={asset}
                                                network={network}
                                                isEditable={isEditable}
                                                onAssetDelete={() => onAssetDelete(postIndex, assetIndex)}
                                                onAssetEdit={(src) => 
                                                    onAssetEdit(postIndex, assetIndex, src)}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {isEditable && 
                            <AddAssetButton 
                                network={network}
                                onAssetNew={(asset) => onAssetNew(postIndex, asset)}
                                onAssetDelete={(assetIndex) => onAssetDelete(postIndex, assetIndex)}
                                post={post}
                                allPosts={allPosts}
                                maxAssets={4} 
                                className="max-w-[100%]"
                            />
                        }
                    </div>
                );
            })}

            {isEditable && (
                <div className="px-4 py-3 pb-2 w-full">
                    <Dropdown
                            disabled={posts?.length === 0 || isLoading}
                            toggleClassName={(!isEditable || !(posts?.length > 1)) && `!cursor-default`}
                            placement={PLACEMENT.TOP_START}
                            parentClassName="w-full"
                            renderTitle={
                                <Button
                                    icon={isLoading ? <MagicSpinner size={20}/> : <HiOutlinePlus />}
                                    variant="plain"
                                    block
                                    disabled={isLoading}
                                    loading={isLoading}
                                    className="border border-tertiary-100 text-tertiary-600 hover:text-black"
                                >
                                    {isLoading ? "Generating..." : "Add new post"}
                                </Button>
                            }
                        >
                            <Dropdown.Item onClick={onCreateFromScratch} className="flex items-center gap-2 py-2 px-4">
                                <HiOutlinePlus className='h-6 md:text-2xl text-primary-600' />
                                <span className="!text-sm">Create new blank post</span>
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => !isLoading && generateNewPostInThread()} className="flex items-center gap-2 py-2 px-4">
                                <BsStars className='h-6 md:text-2xl text-purple-500' />
                                <span className="!text-sm">Generate next thread post with AI</span>
                            </Dropdown.Item>
                        </Dropdown>
                </div>
            )}
        </div>
    );
}

export default BlueskyPreview;
